// Picker modal for the Compare feature. The user clicks Compare from a
// sheet in the viewer; this modal lets them choose which two issuances
// (version sets) of that same sheet number to overlay, plus a color for
// each side. Clicking Compare commits the choice into the store and the
// CompareViewer renders the overlaid result.
//
// Sheets are matched across versions by sheet_number — only VSs that
// actually contain a sheet with the active sheet's number are selectable.

import React, { useEffect, useMemo, useState } from 'react';
import { useStore } from '../store/useStore';
import { getSheets, getSheetThumbnailUrl } from '../utils/api';
import {
  COMPARE_SWATCHES,
  DEFAULT_COLOR_A_ID,
  DEFAULT_COLOR_B_ID,
} from '../utils/compareRender';
import { getCurrentVersionSetId } from '../utils/versionSets';
import styles from './CompareSetupModal.module.css';

export default function CompareSetupModal() {
  const {
    compareSetupOpen, setCompareSetupOpen,
    setCompareConfig,
    project, versionSets, activeSheet,
  } = useStore();

  // All sheets across every VS for the project — needed because the global
  // `sheets` slice is filtered by the active version-set picker. Loaded
  // when the modal opens, refreshed each time so a recently-published VS
  // is picked up.
  const [allSheets, setAllSheets]   = useState([]);
  const [loading,   setLoading]     = useState(true);
  const [colorA,    setColorA]      = useState(DEFAULT_COLOR_A_ID);
  const [colorB,    setColorB]      = useState(DEFAULT_COLOR_B_ID);
  const [vsAId,     setVsAId]       = useState('');
  const [vsBId,     setVsBId]       = useState('');

  const targetSheetNumber = activeSheet?.sheet_number || '';
  const currentVsId = getCurrentVersionSetId(versionSets);

  // Load all-versions sheets list, then seed the VS pickers with current /
  // previous defaults.
  useEffect(() => {
    if (!compareSetupOpen || !project) return;
    let cancelled = false;
    setLoading(true);
    getSheets({ project_id: project.id })
      .then((rows) => {
        if (cancelled) return;
        setAllSheets(rows);
      })
      .catch(console.error)
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [compareSetupOpen, project?.id]);

  // VSs that actually contain a sheet with the target sheet_number — the
  // only ones it makes sense to put in the dropdown.
  const eligibleVersionSets = useMemo(() => {
    if (!targetSheetNumber) return [];
    const vsIdsWithSheet = new Set(
      allSheets
        .filter((s) => s.sheet_number === targetSheetNumber)
        .map((s) => s.version_set_id)
        .filter(Boolean)
    );
    return versionSets.filter((v) => vsIdsWithSheet.has(v.id));
  }, [allSheets, versionSets, targetSheetNumber]);

  // Map (vs_id) -> sheet row for the target sheet_number, so we can look
  // up the thumbnail and the sheet_id to commit into compareConfig.
  const sheetByVsId = useMemo(() => {
    const map = new Map();
    for (const s of allSheets) {
      if (s.sheet_number === targetSheetNumber && s.version_set_id) {
        map.set(s.version_set_id, s);
      }
    }
    return map;
  }, [allSheets, targetSheetNumber]);

  // Seed defaults: A = current VS, B = the VS issued just before it.
  useEffect(() => {
    if (!compareSetupOpen || !eligibleVersionSets.length) return;
    if (vsAId && vsBId) return; // already seeded this open
    const sortedDesc = [...eligibleVersionSets].sort((a, b) => {
      const ad = a.issuance_date || '';
      const bd = b.issuance_date || '';
      return bd.localeCompare(ad);
    });
    const aDefault = currentVsId && eligibleVersionSets.find((v) => v.id === currentVsId)
      ? currentVsId
      : (sortedDesc[0]?.id || '');
    const bDefault = sortedDesc.find((v) => v.id !== aDefault)?.id || '';
    setVsAId(aDefault);
    setVsBId(bDefault);
  }, [compareSetupOpen, eligibleVersionSets, currentVsId, vsAId, vsBId]);

  // Reset state when the modal closes so reopening starts fresh.
  useEffect(() => {
    if (!compareSetupOpen) {
      setVsAId('');
      setVsBId('');
      setColorA(DEFAULT_COLOR_A_ID);
      setColorB(DEFAULT_COLOR_B_ID);
    }
  }, [compareSetupOpen]);

  if (!compareSetupOpen) return null;

  const sheetA = sheetByVsId.get(vsAId);
  const sheetB = sheetByVsId.get(vsBId);
  const canCompare = !!sheetA && !!sheetB && sheetA.id !== sheetB.id;

  function handleCompare() {
    if (!canCompare) return;
    setCompareConfig({
      sheetA_id:    sheetA.id,
      sheetB_id:    sheetB.id,
      colorA_id:    colorA,
      colorB_id:    colorB,
      sheet_number: targetSheetNumber,
    });
    setCompareSetupOpen(false);
  }

  function handleCancel() {
    setCompareSetupOpen(false);
  }

  return (
    <div
      className={styles.backdrop}
      onClick={(e) => e.target === e.currentTarget && handleCancel()}
    >
      <div className={styles.modal}>
        <div className={styles.header}>
          <span className={styles.title}>
            COMPARE — {targetSheetNumber || '(no sheet number)'}
          </span>
          <button className={styles.closeBtn} onClick={handleCancel}>×</button>
        </div>

        <div className={styles.body}>
          {loading ? (
            <div className={styles.loading}>Loading versions…</div>
          ) : eligibleVersionSets.length < 2 ? (
            <div className={styles.empty}>
              <p>Need at least two version sets containing this sheet to compare.</p>
              <p className={styles.emptyHint}>
                {eligibleVersionSets.length === 1
                  ? 'Only one issuance has this sheet right now.'
                  : 'No other issuance has this sheet.'}
              </p>
            </div>
          ) : (
            <div className={styles.sides}>
              <CompareSidePicker
                label="A"
                versionSets={eligibleVersionSets}
                vsId={vsAId}
                onVsChange={setVsAId}
                color={colorA}
                onColorChange={setColorA}
                otherColor={colorB}
                sheet={sheetA}
              />
              <CompareSidePicker
                label="B"
                versionSets={eligibleVersionSets}
                vsId={vsBId}
                onVsChange={setVsBId}
                color={colorB}
                onColorChange={setColorB}
                otherColor={colorA}
                sheet={sheetB}
              />
            </div>
          )}
        </div>

        <div className={styles.footer}>
          <button className={styles.cancelBtn} onClick={handleCancel}>
            CANCEL
          </button>
          <button
            className={styles.compareBtn}
            onClick={handleCompare}
            disabled={!canCompare}
            title={
              !canCompare
                ? 'Pick two different issuances'
                : 'Open the overlaid view'
            }
          >
            COMPARE →
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── One side of the picker (A or B) ────────────────────────────────────────

function CompareSidePicker({
  label, versionSets, vsId, onVsChange, color, onColorChange, otherColor, sheet,
}) {
  const swatch = COMPARE_SWATCHES.find((s) => s.id === color);
  return (
    <div className={styles.side}>
      <div className={styles.sideLabel}>SIDE {label}</div>

      <div className={styles.thumbWrap}>
        {sheet ? (
          <img
            src={getSheetThumbnailUrl(sheet.id, 200)}
            alt={`Sheet ${sheet.sheet_number}`}
            className={styles.thumb}
          />
        ) : (
          <div className={styles.thumbEmpty}>No matching sheet</div>
        )}
      </div>

      <div className={styles.field}>
        <label className={styles.fieldLabel}>Issuance</label>
        <select
          className={styles.select}
          value={vsId}
          onChange={(e) => onVsChange(e.target.value)}
        >
          <option value="" disabled>Pick an issuance…</option>
          {versionSets.map((v) => (
            <option key={v.id} value={v.id}>
              {v.name}
              {v.issuance_date ? ` · ${v.issuance_date}` : ''}
            </option>
          ))}
        </select>
      </div>

      <div className={styles.field}>
        <label className={styles.fieldLabel}>Color</label>
        <div
          className={styles.colorRow}
          style={{ '--swatch-current': swatch ? `rgb(${swatch.rgb.join(',')})` : '#777' }}
        >
          {COMPARE_SWATCHES.map((s) => {
            const isOther = s.id === otherColor;
            const isSelected = s.id === color;
            return (
              <button
                key={s.id}
                type="button"
                className={`${styles.swatch} ${isSelected ? styles.swatchSelected : ''}`}
                style={{ background: `rgb(${s.rgb.join(',')})` }}
                onClick={() => onColorChange(s.id)}
                disabled={isOther}
                title={isOther ? `${s.label} is in use on the other side` : s.label}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}
